import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { WarehouseDto, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreAvailabilityTable from "@store/Components/Store/StoreAvailabilityTable";
import { getStoreSession, getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import { getLoadingOverlayProps } from "@store/theme/common/props/loadingOverlay";
import getBooleanProperty from "@store/utilities/getBooleanProperty";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isStocked?: boolean;
    selectedWarehouse?: WarehouseDto | WarehouseModel | null;
    productContext: ProductContextModel;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const session = getStoreSession(state);
    const settings = getStoreSettingsCollection(state);

    return {
        currentWarehouseName:
            props.selectedWarehouse?.name ||
            (session.fulfillmentMethod === FulfillmentMethod.PickUp
                ? session.pickUpWarehouse?.name
                : session.warehouse.name),
        showGlobalQuantityInventory: settings.poolcorpSettings.showGlobalQuantityInventory,
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityWrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    errorText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    loadingOverlay: getLoadingOverlayProps(),
};

const styles = productAvailabilityStyles;

const StoreProductDetailsAvailability: FC<Props> = ({
    productContext,
    getRealTimeWarehouseInventory,
    currentWarehouseName,
    showGlobalQuantityInventory,
}) => {
    const {
        product,
        productInfo: { unitOfMeasure },
    } = productContext;
    const [warehouses, setWarehouses] = useState<WarehouseDto[]>();
    const [qtyOnHand, setQtyOnHand] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    
    const isHighDemandMode = getBooleanProperty(productContext?.product?.properties?.isHighDemandMode);

    useEffect(() => {
        setIsLoading(true);
        getRealTimeWarehouseInventory({
            productId: product.id,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setQtyOnHand((result as any).qtyOnHand);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    }, [unitOfMeasure]);

    if (errorMessage) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{translate(`${errorMessage}`)}</Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.availabilityWrapper}>
            <LoadingOverlay loading={isLoading} {...styles.loadingOverlay}>
                {!isLoading && !errorMessage
                    ? warehouses && (
                          <StoreAvailabilityTable
                              warehouses={warehouses}
                              currentWarehouseName={currentWarehouseName}
                              qtyOnHand={qtyOnHand}
                              isHighDemandMode={isHighDemandMode}
                              showGlobalQuantityInventory={showGlobalQuantityInventory}
                          />
                      )
                    : null}
            </LoadingOverlay>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreProductDetailsAvailability);
